// apps/web/src/pages/Public/MagicLink/components/ZapsignEmbedFrame.tsx
//
// Embed ZapSign — iframe puro. Não existe SDK/lib da ZapSign para carregar:
// o backend devolve a URL COMPLETA da página de assinatura do signatário e ela
// vai direto no `src`. A URL nunca é montada aqui (base muda entre sandbox e
// produção — concatenar no front viraria bug de ambiente).
//
// `allow="camera; geolocation"`: a modalidade avançada (selfie/liveness) roda
// dentro do iframe quando habilitada na conta.
//
// Eventos: a ZapSign emite `window.postMessage` com `e.data` sendo uma STRING
// simples — `zs-doc-loaded` | `zs-doc-signed` | `zs-signed-file-ready`. Não há
// payload estruturado, token nem documentId, então o evento NÃO correlaciona a
// um documento por si só: a correlação vem do contexto (sabemos qual documento
// embutimos) e a confirmação vem do polling `GET /signature/status`. Os eventos
// aqui são dica de UX (trocar spinner, acelerar o próximo poll) — nunca fonte
// da verdade para marcar algo como assinado.
//
// Todos os eventos chegam DUPLICADOS (POC sandbox: `zs-doc-loaded` 4× e
// `zs-signed-file-ready` 4× numa única sessão), por isso a dedup por tipo de
// evento por sessão de embed é obrigatória — sem ela cada evento vira N
// chamadas ao backend.
//
// Não existe re-mint de chave: o link do signatário não expira e não há
// protocolo de erro 410 como na ClickSign. O retry recarrega o mesmo iframe.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button, cn } from '@alamo/ui';

/** Eventos `postMessage` emitidos pelo embed da ZapSign. */
export const ZAPSIGN_EMBED_EVENTS = [
  'zs-doc-loaded',
  'zs-doc-signed',
  'zs-signed-file-ready',
] as const;

export type ZapsignEmbedEvent = (typeof ZAPSIGN_EMBED_EVENTS)[number];

/**
 * Teto de espera pelo `zs-doc-loaded`. Sem ele, um iframe que nunca carrega
 * (rede caída, URL inválida) deixaria o overlay girando para sempre.
 */
const ZAPSIGN_LOAD_TIMEOUT_MS = 20_000;

function isZapsignEmbedEvent(data: unknown): data is ZapsignEmbedEvent {
  return typeof data === 'string' && (ZAPSIGN_EMBED_EVENTS as readonly string[]).includes(data);
}

export interface ZapsignEmbedFrameProps {
  /**
   * URL COMPLETA da página de assinatura devolvida pelo backend
   * (`envelopeUrl`). Também define a origem aceita no `postMessage`.
   */
  envelopeUrl: string;
  /**
   * Chamado uma única vez por sessão de embed, ao receber `zs-doc-signed`.
   * É uma dica de UX: o consumidor deve reconciliar via `GET /signature/status`.
   */
  onSigned: () => void;
  /** Observador cru dos eventos já deduplicados (telemetria/UX). */
  onEvent?: (event: ZapsignEmbedEvent) => void;
  /** Override do teto de espera pelo `zs-doc-loaded` (ms). Exposto para testes. */
  loadTimeoutMs?: number;
  /**
   * Abaixo de `md`, o iframe estica para ocupar toda a altura do contêiner em
   * vez de usar altura fixa. Exige um pai com altura definida (o `Sheet` com
   * `fillViewport`). A partir de `md` a altura fixa volta.
   */
  fillHeight?: boolean;
}

export function ZapsignEmbedFrame({
  envelopeUrl,
  onSigned,
  onEvent,
  loadTimeoutMs = ZAPSIGN_LOAD_TIMEOUT_MS,
  fillHeight = false,
}: ZapsignEmbedFrameProps) {
  const { t } = useTranslation('signature');

  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [signedHint, setSignedHint] = useState(false);
  // Recarrega o MESMO documento (nova key no iframe). Não há re-mint de chave.
  const [reloadNonce, setReloadNonce] = useState(0);

  // Callbacks em ref: mantêm o listener estável, para que um re-render do
  // consumidor não re-assine o `message` e zere a dedup no meio da sessão.
  const onSignedRef = useRef(onSigned);
  onSignedRef.current = onSigned;
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const expectedOrigin = useMemo(() => {
    try {
      return new URL(envelopeUrl).origin;
    } catch {
      return null;
    }
  }, [envelopeUrl]);

  const handleRetry = useCallback(() => {
    setLoadFailed(false);
    setLoading(true);
    setReloadNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!expectedOrigin || typeof window === 'undefined') return undefined;

    setLoading(true);
    setLoadFailed(false);
    setSignedHint(false);

    // Dedup por tipo de evento, escopada à sessão de embed (documento + reload).
    const processed = new Set<ZapsignEmbedEvent>();

    let loaded = false;
    const watchdog = setTimeout(() => {
      if (loaded) return;
      setLoading(false);
      setLoadFailed(true);
    }, loadTimeoutMs);

    function handleMessage(event: MessageEvent) {
      // Nunca confiar em postMessage de origem arbitrária.
      if (event.origin !== expectedOrigin) return;
      if (!isZapsignEmbedEvent(event.data)) return;
      if (processed.has(event.data)) return;
      processed.add(event.data);

      switch (event.data) {
        case 'zs-doc-loaded':
          loaded = true;
          clearTimeout(watchdog);
          setLoading(false);
          setLoadFailed(false);
          break;
        case 'zs-doc-signed':
          setSignedHint(true);
          onSignedRef.current();
          break;
        case 'zs-signed-file-ready':
          break;
      }

      onEventRef.current?.(event.data);
    }

    window.addEventListener('message', handleMessage);
    return () => {
      clearTimeout(watchdog);
      window.removeEventListener('message', handleMessage);
    };
  }, [expectedOrigin, envelopeUrl, reloadNonce, loadTimeoutMs]);

  if (!expectedOrigin) {
    return (
      <div
        className="flex flex-col gap-3 rounded-lg border border-erro-apoio/20 bg-erro-apoio/5 p-3 text-sm text-erro-apoio"
        role="alert"
        data-testid="zapsign-error"
      >
        <span>{t('messages.iframe_error')}</span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative w-full',
        fillHeight && 'flex min-h-0 flex-1 flex-col md:block md:flex-none',
      )}
      data-testid="zapsign-embed-frame"
    >
      {loading && !loadFailed && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center bg-white"
          data-testid="zapsign-loading"
        >
          <div className="flex flex-col items-center gap-2 text-ink/50">
            <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
            <p className="text-sm">{t('messages.iframe_loading')}</p>
          </div>
        </div>
      )}
      {loadFailed && (
        <div
          className="flex flex-col gap-3 rounded-lg border border-erro-apoio/20 bg-erro-apoio/5 p-3 text-sm text-erro-apoio"
          role="alert"
          data-testid="zapsign-error"
        >
          <span>{t('messages.iframe_error')}</span>
          <Button
            variant="secondary"
            type="button"
            onClick={handleRetry}
            data-testid="zapsign-retry"
          >
            {t('actions.retry')}
          </Button>
        </div>
      )}
      {!loadFailed && (
        <>
          {/* O fluxo tem várias etapas (ver → identidade → assinar → código de
              6 letras → conclusão) e precisa de espaço. Com `fillHeight` o
              iframe recebe a altura útil inteira abaixo de `md`, para que só
              exista o scroll de dentro dele — o de fora competiria com o gesto
              de desenhar a assinatura no canvas. Sem `fillHeight`, altura fixa
              com teto em `dvh`. */}
          <iframe
            key={`${envelopeUrl}#${reloadNonce}`}
            src={envelopeUrl}
            title={t('zapsign.frame_title')}
            allow="camera; geolocation"
            className={cn(
              'w-full border-0',
              fillHeight
                ? 'min-h-0 flex-1 rounded-none md:h-[680px] md:max-h-[78dvh] md:flex-none md:rounded-lg'
                : 'h-[680px] max-h-[78dvh] rounded-lg',
            )}
            data-testid="zapsign-iframe"
          />
          {signedHint ? (
            <p
              className="mt-2 shrink-0 text-center text-xs text-ink/60"
              data-testid="zapsign-signed-processing"
            >
              {t('zapsign.confirming')}
            </p>
          ) : (
            <p
              className={cn(
                'mt-2 shrink-0 text-center text-xs text-ink/60',
                fillHeight && 'px-3 pb-1 md:px-0 md:pb-0',
              )}
              data-testid="zapsign-otp-hint"
            >
              {t('zapsign.otp_hint')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
